import { AfterViewInit, Component, ElementRef, HostListener, Injector, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatChannelTypeEnum,
  ChatConversation,
  ChatSearchPerson,
  ChatService,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { Subject, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { conversationsSelector, sortedConversationsSelector, userDisplaySelector } from '../state/chat.selectors';
import { chatMediaPreview, stripChatMarkdown } from '../pipes/chat-preview.util';
import { fuzzyScore } from './chat-fuzzy.util';
import { openChatConversation, openChatDirect } from './chat-open-target.util';
import { ChatShellService } from '../services/chat-shell.service';
import { resolveChatInitials } from '../chat-initials.util';

type ChatSwitcherRowKind = 'conversation' | 'person' | 'action';

/** Flat, keyboard-navigable row (sections are rendered from the same array). */
interface ChatSwitcherRow {
  kind: ChatSwitcherRowKind;
  /** channelId for conversations · userId for people · '' for the action row */
  id: string;
  label: string;
  subtitle: string;
  icon: string;
  image?: string;
  initial?: string;
  score: number;
}

/** People search needs a term — one character is too noisy to query on. */
const PEOPLE_SEARCH_MIN_LENGTH = 2;
const PEOPLE_SEARCH_LIMIT = 8;
const CONVERSATION_RESULT_LIMIT = 12;

/**
 * Quick switcher (SPEC-2026-07-internal-chat, T-22 / RF-21 / AC-17).
 *
 * Opened with Ctrl/Cmd+K from ANY authenticated route — the shortcut is
 * registered globally by ChatSwitcherService, so it works both in the `/chat`
 * full view and while the bubble popup is open. Rendered as a Hyper-styled
 * MatDialog overlay.
 *
 * Two data sources, both already scoped:
 * - conversations come from the bootstrap store (activity-sorted, so the
 *   empty-term list is recent-first) and are filtered locally;
 * - people come from `GET /api/chat/search` (`data.people`), company-scoped
 *   server-side. Picking a person opens the existing DM or a DM draft.
 *
 * Keyboard: ↑/↓ move, Enter opens, Esc closes.
 */
@Component({
  templateUrl: './chat-switcher.component.html',
  styleUrls: ['./chat-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatSwitcherComponent extends AbstractComponent implements OnInit, AfterViewInit {

  @ViewChildren('switcherRow') rowElements!: QueryList<ElementRef<HTMLElement>>;

  term = '';
  conversationRows: ChatSwitcherRow[] = [];
  peopleRows: ChatSwitcherRow[] = [];
  actionRow: ChatSwitcherRow | null = null;
  rows: ChatSwitcherRow[] = [];
  activeIndex = 0;
  loadingPeople = false;

  private allConversations: ChatConversation[] = [];
  private sortedConversations: ChatConversation[] = [];
  private userDisplay: ChatUserDisplayMap = {};
  private readonly terms = new Subject<string>();

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatSwitcherComponent>,
    private readonly store: Store,
    private readonly chatService: ChatService,
    private readonly chatShell: ChatShellService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(sortedConversationsSelector),
      this.store.select(conversationsSelector),
      this.store.select(userDisplaySelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([sorted, conversations, userDisplay]) => {
        this.sortedConversations = sorted;
        this.allConversations = conversations;
        this.userDisplay = userDisplay;
        this.applyFilter();
      });

    // People are only reachable through the membership-scoped search endpoint.
    this.terms
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.trim().length < PEOPLE_SEARCH_MIN_LENGTH) {
            this.loadingPeople = false;
            return of<ChatSearchPerson[]>([]);
          }
          this.loadingPeople = true;
          return this.chatService.search({ q: term.trim(), limit: PEOPLE_SEARCH_LIMIT })
            .pipe(
              map((envelope) => envelope?.data?.people ?? []),
              catchError(() => of<ChatSearchPerson[]>([])),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((people) => {
        this.loadingPeople = false;
        this.peopleRows = this.buildPeopleRows(people);
        this.composeRows();
      });
  }

  ngAfterViewInit(): void {
    // MatDialog focuses the first tabbable element (the search input) already;
    // nothing else to do beyond keeping the active row visible.
    this.scrollActiveIntoView();
  }

  onTermChange(): void {
    this.applyFilter();
    this.terms.next(this.term);
  }

  /** ↑/↓ move · Enter opens · Esc closes (MatDialog also closes on Esc). */
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.activate(this.rows[this.activeIndex]);
        break;
      case 'Escape':
        this.close();
        break;
      default:
        break;
    }
  }

  setActive(row: ChatSwitcherRow): void {
    const index = this.rows.indexOf(row);
    if (index >= 0) this.activeIndex = index;
  }

  isActive(row: ChatSwitcherRow): boolean {
    return this.rows[this.activeIndex] === row;
  }

  activate(row: ChatSwitcherRow | undefined): void {
    if (!row) return;

    switch (row.kind) {
      case 'conversation':
        openChatConversation(this.store, this.chatShell, row.id);
        break;
      case 'person':
        openChatDirect(this.store, this.chatShell, this.getIDCurrentUser(), row.id, this.allConversations);
        break;
      case 'action':
        this.chatShell.open({ view: 'search', searchTerm: this.term.trim() });
        break;
      default:
        return;
    }

    this.close();
  }

  close(): void {
    this.dialogRef.close();
  }

  trackRow(_index: number, row: ChatSwitcherRow): string {
    return `${row.kind}:${row.id}`;
  }

  private applyFilter(): void {
    const term = this.term.trim();
    const currentUserId = this.getIDCurrentUser();

    this.conversationRows = this.sortedConversations
      .map((conversation) => {
        const label = this.resolveName(conversation, currentUserId);
        const media = chatMediaPreview(conversation.lastMessagePreview);
        return {
          row: <ChatSwitcherRow>{
            kind: 'conversation',
            id: conversation._id,
            label,
            // Same treatment as the conversation lists: media tokens become a
            // translated word, markdown markers are stripped off the text.
            subtitle: media
              ? this.t(media.labelKey)
              : stripChatMarkdown(conversation.lastMessagePreview),
            icon: this.resolveIcon(conversation.type),
            image: conversation.type === ChatChannelTypeEnum.DIRECT
              ? this.userDisplay[this.counterpartId(conversation, currentUserId)]?.image
              : undefined,
            initial: resolveChatInitials(label || '?'),
            score: fuzzyScore(label, term),
          },
        };
      })
      .filter((entry) => entry.row.score >= 0)
      // Empty term keeps the store order (recent-first); a term ranks by score.
      .sort((a, b) => (term ? b.row.score - a.row.score : 0))
      .slice(0, CONVERSATION_RESULT_LIMIT)
      .map((entry) => entry.row);

    if (!term) {
      this.peopleRows = [];
      this.loadingPeople = false;
    }

    this.composeRows();
  }

  private buildPeopleRows(people: ChatSearchPerson[]): ChatSwitcherRow[] {
    const currentUserId = this.getIDCurrentUser();

    return (people ?? [])
      .filter((person) => !!person?._id && !!person?.name)
      .map((person) => ({
        kind: <ChatSwitcherRowKind>'person',
        id: person._id,
        label: person.name,
        subtitle: person._id === currentUserId ? this.t('chat.switcher.yourself') : (person.email ?? ''),
        icon: 'ri-user-3-line',
        image: person.image,
        initial: resolveChatInitials(person.name || '?'),
        score: fuzzyScore(person.name, this.term),
      }));
  }

  private composeRows(): void {
    const term = this.term.trim();
    this.actionRow = term
      ? {
        kind: 'action',
        id: '',
        label: this.t('chat.switcher.searchMessages', { term }),
        subtitle: '',
        icon: 'ri-search-2-line',
        score: 0,
      }
      : null;

    this.rows = [
      ...this.conversationRows,
      ...this.peopleRows,
      ...(this.actionRow ? [this.actionRow] : []),
    ];

    if (this.activeIndex >= this.rows.length) this.activeIndex = Math.max(0, this.rows.length - 1);
  }

  private move(delta: number): void {
    if (this.rows.length === 0) return;
    this.activeIndex = (this.activeIndex + delta + this.rows.length) % this.rows.length;
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      const element = this.rowElements?.get(this.activeIndex)?.nativeElement;
      element?.scrollIntoView({ block: 'nearest' });
    });
  }

  private counterpartId(conversation: ChatConversation, currentUserId: string): string {
    return (conversation.members ?? []).find((id) => id !== currentUserId) ?? currentUserId;
  }

  /** Same naming rules as the sidebar/popup list (DIRECT/GROUP are unnamed). */
  private resolveName(conversation: ChatConversation, currentUserId: string): string {
    switch (conversation.type) {
      case ChatChannelTypeEnum.DIRECT:
        return this.userDisplay[this.counterpartId(conversation, currentUserId)]?.name
          ?? this.t('chat.bubble.directConversation');
      case ChatChannelTypeEnum.GROUP: {
        if (conversation.name) return conversation.name;
        const names = (conversation.members ?? [])
          .filter((id) => id !== currentUserId)
          .map((id) => this.userDisplay[id]?.name)
          .filter((name): name is string => !!name);
        return names.length > 0 ? names.slice(0, 3).join(', ') : this.t('chat.bubble.groupConversation');
      }
      default:
        return conversation.name ?? this.t('chat.bubble.channelConversation');
    }
  }

  private resolveIcon(type: ChatChannelTypeEnum): string {
    switch (type) {
      case ChatChannelTypeEnum.DIRECT: return 'ri-user-3-line';
      case ChatChannelTypeEnum.GROUP: return 'ri-group-line';
      case ChatChannelTypeEnum.PRIVATE: return 'ri-lock-line';
      default: return 'ri-hashtag';
    }
  }
}
